import React, { useState, useEffect } from "react";
import { Row, Col, Card, CardBody } from "shards-react";
import { Table, Select, Button, Alert } from "antd";
import PageTitle from "../_components/PageTitle";
import axios from "axios";
import { API_URL } from "../_config";

const { Option } = Select;
const CLASSIFIEDS = API_URL + "api/classifieds/";

/**
 * Guía de precios de la CCA dentro del admin.
 *
 * Los endpoints son públicos (no piden token), así que esto es todo front.
 *
 * OJO con los parámetros, que no son uniformes:
 *   - listamarcas                      -> devuelve [{name}]
 *   - listamodelos/{NOMBRE de marca}   -> devuelve [{name, id}]
 *   - listaversionanio/{ID modelo}/{año}
 *
 * El último recibe el **id** del modelo, no el nombre: el controller hace
 * `where('list_anio.id_modelo', '=', $q)`. Si le mandás el nombre NO falla —
 * MySQL castea el string a número y te devuelve precios de otro vehículo.
 */

const columnas = [
  { title: "Versión", dataIndex: "version", key: "version" },
  {
    title: "Precio",
    dataIndex: "precio",
    key: "precio",
    width: 180,
    align: "right",
  },
];

const ListaDePrecios = () => {
  const [marcas, setMarcas] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [anios, setAnios] = useState([]);
  const [precios, setPrecios] = useState(undefined);

  const [marca, setMarca] = useState(undefined);
  const [modeloId, setModeloId] = useState(undefined);
  const [anio, setAnio] = useState(undefined);

  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");

  const pedir = (url) =>
    axios.get(url).then((r) => (Array.isArray(r.data) ? r.data : []));

  // Marcas
  useEffect(() => {
    setCargando(true);
    pedir(CLASSIFIEDS + "listamarcas")
      // La tabla trae una marca con nombre vacío; no tiene sentido ofrecerla.
      .then((data) => setMarcas(data.filter((m) => m && m.name)))
      .catch(() => setError("No se pudieron cargar las marcas."))
      .finally(() => setCargando(false));
  }, []);

  const elegirMarca = (valor) => {
    setMarca(valor);
    setModeloId(undefined);
    setAnio(undefined);
    setModelos([]);
    setAnios([]);
    setPrecios(undefined);
    setError("");

    setCargando(true);
    pedir(CLASSIFIEDS + "listamodelos/" + encodeURIComponent(valor))
      .then((data) => setModelos(data.filter((m) => m && m.name)))
      .catch(() => setError("No se pudieron cargar los modelos de " + valor + "."))
      .finally(() => setCargando(false));
  };

  const elegirModelo = (valor) => {
    setModeloId(valor);
    setAnio(undefined);
    setAnios([]);
    setPrecios(undefined);
    setError("");

    setCargando(true);
    // valor es el ID del modelo. Ver el comentario de arriba.
    pedir(CLASSIFIEDS + "listaversionanio/" + encodeURIComponent(valor) + "/null")
      .then(setAnios)
      .catch(() => setError("No se pudieron cargar los años."))
      .finally(() => setCargando(false));
  };

  const elegirAnio = (valor) => {
    setAnio(valor);
    setPrecios(undefined);
    setError("");

    setCargando(true);
    pedir(
      CLASSIFIEDS +
        "listaversionanio/" +
        encodeURIComponent(modeloId) +
        "/" +
        encodeURIComponent(valor)
    )
      .then(setPrecios)
      .catch(() => setError("No se pudieron cargar los precios."))
      .finally(() => setCargando(false));
  };

  const limpiar = () => {
    setMarca(undefined);
    setModeloId(undefined);
    setAnio(undefined);
    setModelos([]);
    setAnios([]);
    setPrecios(undefined);
    setError("");
  };

  const filtroPorTexto = (entrada, option) =>
    String(option.props.children).toLowerCase().indexOf(String(entrada).toLowerCase()) >= 0;

  return (
    <div>
      <Row noGutters className="page-header py-4" style={{ width: "100%" }}>
        <PageTitle sm="8" title="Lista de precios" className="text-sm-left" />
        <Col sm="4" className="text-sm-right d-none d-sm-block">
          {(marca || precios) && (
            <Button onClick={limpiar}>Nueva búsqueda</Button>
          )}
        </Col>
      </Row>

      <Row>
        <Col>
          <Card small className="mb-4">
            <CardBody>
              <Row>
                <Col md="4" className="mb-2">
                  <Select
                    showSearch
                    style={{ width: "100%" }}
                    placeholder="Seleccioná la marca"
                    value={marca}
                    onChange={elegirMarca}
                    filterOption={filtroPorTexto}
                  >
                    {marcas.map((m) => (
                      <Option key={m.name} value={m.name}>
                        {m.name}
                      </Option>
                    ))}
                  </Select>
                </Col>

                <Col md="4" className="mb-2">
                  <Select
                    showSearch
                    style={{ width: "100%" }}
                    placeholder="Seleccioná el modelo"
                    value={modeloId}
                    onChange={elegirModelo}
                    disabled={!marca || modelos.length === 0}
                    filterOption={filtroPorTexto}
                  >
                    {modelos.map((m) => (
                      <Option key={m.id} value={m.id}>
                        {m.name}
                      </Option>
                    ))}
                  </Select>
                </Col>

                <Col md="4" className="mb-2">
                  <Select
                    showSearch
                    style={{ width: "100%" }}
                    placeholder="Seleccioná el año"
                    value={anio}
                    onChange={elegirAnio}
                    disabled={!modeloId || anios.length === 0}
                    filterOption={filtroPorTexto}
                  >
                    {anios.map((a) => (
                      <Option key={a.name} value={a.name}>
                        {a.name}
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>

              {error !== "" && (
                <Alert type="error" message={error} showIcon className="mt-3" />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {precios && (
        <Row>
          <Col>
            <Card small className="mb-4">
              <CardBody className="p-0 pb-3">
                <div className="table-responsive">
                  <Table
                    rowKey={(r, i) => i}
                    columns={columnas}
                    dataSource={precios}
                    loading={cargando}
                    pagination={false}
                    locale={{
                      emptyText: "No hay precios cargados para esa combinación.",
                    }}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default ListaDePrecios;

export { ListaDePrecios };
